import { useMemo, useState } from 'react';
import { formatHours } from '@chronos/shared';
import { useDashboard } from '@/hooks/queries';
import { Card, Dot, EmptyState, ErrorNote, PageHeader, Spinner, StatTile } from '@/components/ui';
import { addDays, formatTime, relativeTime, startOfDay, startOfWeek, toDateInput } from '@/lib/format';

type RangeKey = 'today' | 'week' | 'month';

const RANGES: { key: RangeKey; label: string; from: () => Date }[] = [
  { key: 'today', label: 'Today', from: () => startOfDay() },
  { key: 'week', label: 'This week', from: () => startOfWeek() },
  { key: 'month', label: 'Last 30 days', from: () => addDays(startOfDay(), -30) },
];

export function DashboardPage() {
  const [range, setRange] = useState<RangeKey>('week');
  const from = useMemo(() => RANGES.find((r) => r.key === range)!.from().toISOString(), [range]);
  const to = useMemo(() => addDays(startOfDay(), 1).toISOString(), []);

  const { data, isLoading, error } = useDashboard(from, to);
  const maxProject = Math.max(1, ...(data?.byProject ?? []).map((p) => p.seconds));

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Dashboard"
        description="Who is working right now, and where the hours are going."
        actions={
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`rounded-md px-3 py-1.5 text-sm transition ${range === r.key ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      <ErrorNote error={error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Clocked in now" value={String(data?.activeUsers.length ?? 0)} tone="green" hint="active timers" />
            <StatTile label="Today" value={formatHours(data?.totals.todaySeconds ?? 0)} />
            <StatTile label="This week" value={formatHours(data?.totals.weekSeconds ?? 0)} />
            <StatTile label="Screenshots today" value={String(data?.screenshotsToday ?? 0)} />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Card>
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h2 className="text-sm font-semibold text-slate-800">Active right now</h2>
                <span className="badge bg-emerald-100 text-emerald-800">{data?.activeUsers.length ?? 0} live</span>
              </div>
              {data?.activeUsers.length ? (
                <ul className="divide-y divide-slate-100">
                  {data.activeUsers.map((u) => (
                    <li key={u.userId} className="flex items-center gap-3 px-4 py-3">
                      <span className="relative flex h-2 w-2" aria-hidden="true">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">{u.name}</p>
                        <p className="truncate text-xs text-slate-500">{u.projectName}</p>
                      </div>
                      <span className="badge bg-slate-100 text-slate-600">{u.source.toLowerCase()}</span>
                      <span className="text-xs text-slate-500" title={formatTime(u.startedAt)}>{relativeTime(u.startedAt)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="Nobody is clocked in" description="Active timers appear here as soon as someone starts one." />
              )}
            </Card>

            <Card>
              <div className="border-b border-slate-200 px-4 py-3">
                <h2 className="text-sm font-semibold text-slate-800">Hours by project</h2>
              </div>
              {data?.byProject.length ? (
                <ul className="space-y-3 px-4 py-4">
                  {data.byProject.map((p) => (
                    <li key={p.projectId}>
                      <div className="mb-1 flex items-center gap-2">
                        <Dot color={p.color} />
                        <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{p.projectName}</span>
                        <span className="font-mono text-xs tabular-nums text-slate-600">{formatHours(p.seconds)}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${(p.seconds / maxProject) * 100}%`, backgroundColor: p.color }} />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="No time tracked in this range" />
              )}
            </Card>
          </div>

          <Card className="mt-4">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-800">Hours by person</h2>
            </div>
            {data?.byUser.length ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="th">Person</th>
                      <th className="th text-right">Hours</th>
                      <th className="th w-1/2">Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.byUser.map((u) => {
                      const share = (u.seconds / Math.max(1, data.totals.rangeSeconds)) * 100;
                      return (
                        <tr key={u.userId}>
                          <td className="td font-medium">{u.userName}</td>
                          <td className="td text-right font-mono tabular-nums">{formatHours(u.seconds)}</td>
                          <td className="td">
                            <div className="flex items-center gap-2">
                              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                                <div className="h-full rounded-full bg-brand-500" style={{ width: `${share}%` }} />
                              </div>
                              <span className="w-10 text-right text-xs tabular-nums text-slate-500">{share.toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No time tracked in this range" />
            )}
          </Card>

          <p className="mt-3 text-xs text-slate-400">
            Range: {toDateInput(new Date(from))} → {toDateInput(new Date(to))}. Managers see only the projects they manage.
          </p>
        </>
      )}
    </div>
  );
}
